import { useEffect, useState } from "react";

export function useMediaQuery(query: string) {
  const [matches, setMatches] = useState<boolean>(
    globalThis.matchMedia(query).matches,
  );

  useEffect(() => {
    const media = globalThis.matchMedia(query);
    const listener = () => {
      setMatches(media.matches);
    };
    window.addEventListener("resize", listener);
    return () => window.removeEventListener("resize", listener);
  }, [query]);

  return matches;
}

const WindowSizeArray = [
  "4xl",
  "3xl",
  "2xl",
  "xl",
  "lg",
  "md",
  "sm",
  "xs",
] as const;
export type WindowSize = (typeof WindowSizeArray)[number];
const windowSizes: Record<WindowSize, number> = {
  "4xl": 2048,
  "3xl": 1792,
  "2xl": 1536,
  xl: 1280,
  lg: 1024,
  md: 768,
  sm: 640,
  xs: 0,
};

function detectSize(ref?: HTMLElement | null): WindowSize {
  const width = ref == null ? window.innerWidth : ref.clientWidth;
  const index = Object.values(windowSizes).findIndex((value) => width >= value);
  return WindowSizeArray[index];
}

function useSize(ref?: HTMLElement | null) {
  const [windowSize, setWindowSize] = useState<WindowSize>(detectSize(ref));

  useEffect(() => {
    if (ref == null) {
      const listener = () => setWindowSize(detectSize(ref));
      window.addEventListener("resize", listener);
      return () => window.removeEventListener("resize", listener);
    } else {
      const observer = new ResizeObserver(() => setWindowSize(detectSize(ref)));
      observer.observe(ref);
      return () => observer.disconnect();
    }
  }, [ref]);

  return windowSize;
}

export function useMinWindowSize(size: WindowSize) {
  const windowSize = useSize();

  const windowSizeIndex = WindowSizeArray.indexOf(windowSize);
  const sizeIndex = WindowSizeArray.indexOf(size);

  return windowSizeIndex <= sizeIndex;
}

export function useMaxWindowSize(size: WindowSize) {
  const windowSize = useSize();

  const windowSizeIndex = WindowSizeArray.indexOf(windowSize);
  const sizeIndex = WindowSizeArray.indexOf(size);

  return windowSizeIndex >= sizeIndex;
}

export function useWindowSize(): WindowSize {
  return useSize();
}

export function useRawWindowSize() {
  const [size, setSize] = useState({
    height: window.innerHeight,
    width: window.innerWidth,
  });

  useEffect(() => {
    const listener = () => {
      setSize({
        height: window.innerHeight,
        width: window.innerWidth,
      });
    };
    window.addEventListener("resize", listener);
    return () => window.removeEventListener("resize", listener);
  }, []);

  return size;
}

export function useElementSize(ref: HTMLElement | null): WindowSize {
  return useSize(ref);
}

export function useMinElementSize(ref: HTMLElement | null, size: WindowSize) {
  const elementSize = useSize(ref);

  const elementSizeIndex = WindowSizeArray.indexOf(elementSize);
  const sizeIndex = WindowSizeArray.indexOf(size);

  return elementSizeIndex <= sizeIndex;
}
